import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Tabs, Tab, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, CircularProgress, Select, MenuItem,
  FormControl, InputLabel, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  PushPin, Lock, VisibilityOff, Visibility, CheckCircle, Cancel, Pending,
  Add, SwapHoriz
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import forumService from '../../services/forumService';
import DialogHeader from '../../components/Common/DialogHeader';
import RowActionsMenu from '../../components/Admin/shared/RowActionsMenu';

const formatDate = (d) => {
  try { return formatDistanceToNow(parseISO(d), { addSuffix: true, locale: hu }); }
  catch { return ''; }
};

// ─── BEJELENTÉSEK TAB ────────────────────────────────────────────────────────

const ReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await forumService.getReports({ status: statusFilter });
      if (res.success) setReports(res.data.reports);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await forumService.updateReportStatus(id, status);
    load();
  };

  const statusColor = { PENDING: 'warning', REVIEWED: 'info', RESOLVED: 'success', DISMISSED: 'default' };
  const statusLabel = { PENDING: 'Várakozik', REVIEWED: 'Átnézett', RESOLVED: 'Megoldva', DISMISSED: 'Elvetve' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Bejelentések</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Státusz</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Státusz">
            <MenuItem value="PENDING">Várakozó</MenuItem>
            <MenuItem value="REVIEWED">Átnézett</MenuItem>
            <MenuItem value="RESOLVED">Megoldva</MenuItem>
            <MenuItem value="DISMISSED">Elvetve</MenuItem>
            <MenuItem value="ALL">Mind</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <CircularProgress />}
      {!loading && reports.length === 0 && (
        <Alert severity="success">Nincsenek bejelentések ebben a státuszban.</Alert>
      )}

      {!loading && reports.length > 0 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tartalom</TableCell>
                <TableCell>Ok</TableCell>
                <TableCell>Bejelentő</TableCell>
                <TableCell>Dátum</TableCell>
                <TableCell>Státusz</TableCell>
                <TableCell>Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map(report => (
                <TableRow key={report.id}>
                  <TableCell>
                    {report.thread && (
                      <Link to={`/forum/tema/${report.thread.id}`} target="_blank" style={{ color: 'inherit' }}>
                        📝 {report.thread.title}
                      </Link>
                    )}
                    {report.reply && (
                      <Link to={`/forum/tema/${report.reply.threadId}`} target="_blank" style={{ color: 'inherit' }}>
                        💬 {report.reply.content?.substring(0, 60)}...
                      </Link>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>{report.reason}</Typography>
                  </TableCell>
                  <TableCell>
                    {report.user?.firstName} {report.user?.lastName}
                  </TableCell>
                  <TableCell>{formatDate(report.createdAt)}</TableCell>
                  <TableCell>
                    <Chip label={statusLabel[report.status]} size="small" color={statusColor[report.status]} />
                  </TableCell>
                  <TableCell>
                    {report.status === 'PENDING' && (
                      <RowActionsMenu
                        primaryAction={{
                          icon: <CheckCircle fontSize="small" />,
                          color: 'success',
                          iconOnly: true,
                          title: 'Megoldva',
                          onClick: () => updateStatus(report.id, 'RESOLVED'),
                        }}
                        menuActions={[
                          { label: 'Elvetve', icon: <Cancel fontSize="small" />, onClick: () => updateStatus(report.id, 'DISMISSED') },
                          { label: 'Átnézett', icon: <Pending fontSize="small" />, onClick: () => updateStatus(report.id, 'REVIEWED') },
                        ]}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

// ─── TÉMÁK TAB ───────────────────────────────────────────────────────────────

const ThreadsTab = () => {
  const [threads, setThreads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveCategoryId, setMoveCategoryId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [threadsRes, catRes] = await Promise.all([
        forumService.getThreads({ limit: 100, sort: 'new', includeHidden: true }),
        forumService.getCategories()
      ]);
      if (threadsRes.success) setThreads(threadsRes.data.threads);
      if (catRes.success) setCategories(catRes.data.categories);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePin = async (id) => { await forumService.pinThread(id); load(); };
  const handleLock = async (id) => { await forumService.lockThread(id); load(); };
  const handleHide = async (id) => { await forumService.hideThread(id); load(); };

  const openMoveDialog = (thread) => {
    setMoveTarget(thread);
    setMoveCategoryId(String(thread.category?.id ?? ''));
    setMoveDialogOpen(true);
  };

  const handleMove = async () => {
    if (!moveCategoryId || !moveTarget) return;
    await forumService.moveThread(moveTarget.id, parseInt(moveCategoryId));
    setMoveDialogOpen(false);
    setMoveTarget(null);
    load();
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Témák kezelése</Typography>
      {loading && <CircularProgress />}
      {!loading && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cím</TableCell>
                <TableCell>Kategória</TableCell>
                <TableCell>Szerző</TableCell>
                <TableCell>Válaszok</TableCell>
                <TableCell>Állapot</TableCell>
                <TableCell>Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {threads.map(thread => (
                <TableRow
                  key={thread.id}
                  sx={thread.isHidden ? { opacity: 0.5, bgcolor: alpha('#f44336', 0.04) } : {}}
                >
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Link to={`/forum/tema/${thread.id}`} target="_blank" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <Typography variant="body2" noWrap fontWeight={500}>{thread.title}</Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{thread.category?.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {thread.isAnonymous ? 'Névtelen' : `${thread.author?.firstName} ${thread.author?.lastName}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{thread.replyCount}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {thread.isPinned && <Chip label="Kitűzött" size="small" color="warning" />}
                      {thread.isLocked && <Chip label="Lezárt" size="small" />}
                      {thread.isHidden && <Chip label="Rejtett" size="small" color="error" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <RowActionsMenu
                      primaryAction={{
                        icon: <PushPin fontSize="small" />,
                        color: thread.isPinned ? 'warning' : 'default',
                        iconOnly: true,
                        title: thread.isPinned ? 'Kitűzés levétele' : 'Kitűzés',
                        onClick: () => handlePin(thread.id),
                      }}
                      menuActions={[
                        { label: thread.isLocked ? 'Lezárás feloldása' : 'Lezárás', icon: <Lock fontSize="small" />, onClick: () => handleLock(thread.id) },
                        {
                          label: thread.isHidden ? 'Visszahozás' : 'Elrejtés',
                          icon: thread.isHidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />,
                          color: thread.isHidden ? 'success' : 'error',
                          onClick: () => handleHide(thread.id),
                        },
                        { label: 'Áthelyezés másik kategóriába', icon: <SwapHoriz fontSize="small" />, onClick: () => openMoveDialog(thread) },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Move dialog */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<SwapHoriz />} title="Téma áthelyezése" onClose={() => setMoveDialogOpen(false)} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Téma: <strong>{moveTarget?.title}</strong>
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Célkategória</InputLabel>
            <Select value={moveCategoryId} onChange={e => setMoveCategoryId(e.target.value)} label="Célkategória">
              {categories.map(cat => (
                <MenuItem key={cat.id} value={String(cat.id)}>{cat.icon} {cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setMoveDialogOpen(false)}>Mégse</Button>
          <Button variant="contained" onClick={handleMove} disabled={!moveCategoryId || moveCategoryId === String(moveTarget?.category?.id)}>
            Áthelyezés
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── KATEGÓRIÁK TAB ──────────────────────────────────────────────────────────

const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', icon: '', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await forumService.getCategories();
      if (res.success) setCategories(res.data.categories);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpen = () => {
    setForm({ name: '', description: '', icon: '', sortOrder: categories.length });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('A név megadása kötelező.'); return; }
    setSaving(true);
    try {
      const res = await forumService.createCategory({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        icon: form.icon.trim() || undefined,
        sortOrder: parseInt(form.sortOrder) || 0
      });
      if (res.success) {
        setDialogOpen(false);
        load();
      } else {
        setError(res.message || 'Hiba történt.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Fórum kategóriák</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleOpen}>
          Új kategória
        </Button>
      </Box>

      {loading && <CircularProgress />}
      {!loading && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ikon</TableCell>
                <TableCell>Név</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Leírás</TableCell>
                <TableCell>Sorrend</TableCell>
                <TableCell>Témák</TableCell>
                <TableCell>Státusz</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map(cat => (
                <TableRow key={cat.id}>
                  <TableCell><Typography fontSize={20}>{cat.icon}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{cat.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{cat.slug}</Typography></TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap color="text.secondary">{cat.description}</Typography>
                  </TableCell>
                  <TableCell>{cat.sortOrder}</TableCell>
                  <TableCell>{cat._count?.threads ?? 0}</TableCell>
                  <TableCell>
                    <Chip label={cat.isActive ? 'Aktív' : 'Inaktív'} size="small" color={cat.isActive ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Új kategória dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Add />} title="Új fórum kategória" onClose={() => setDialogOpen(false)} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Név *"
            fullWidth
            size="small"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Leírás"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
          <TextField
            label="Ikon (emoji)"
            fullWidth
            size="small"
            value={form.icon}
            onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
            placeholder="pl. 📸"
          />
          <TextField
            label="Rendezési sorrend"
            fullWidth
            size="small"
            type="number"
            value={form.sortOrder}
            onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setDialogOpen(false)}>Mégse</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── FŐ KOMPONENS ────────────────────────────────────────────────────────────

const ForumModeration = () => {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" fontFamily="Playfair Display, serif" fontWeight={700} gutterBottom>
        Fórum moderálás
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Bejelentések" />
          <Tab label="Témák" />
          <Tab label="Kategóriák" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {tab === 0 && <ReportsTab />}
          {tab === 1 && <ThreadsTab />}
          {tab === 2 && <CategoriesTab />}
        </Box>
      </Paper>
    </Container>
  );
};

export default ForumModeration;
